import EdEdupad from '@/components/common/ed-edupad/ed-edupad';
import Component from 'vue-class-component';
import EduacPadClient from '@webcat/eduacpad-client';
import { Prop, Emit, Ref, Watch } from 'vue-property-decorator';
import tinyColor from 'tinycolor2';
import Quill from 'quill';
import QuillCursors from 'quill-cursors';
import mathquill4quill from 'mathquill4quill';
import 'mathquill4quill/mathquill4quill.css';
import 'mathquill/build/mathquill';
import 'mathquill/build/mathquill.css';
import katex from 'katex';
import 'katex/dist/katex.css';
import { setQuillInlineStyle } from '@/components/common/ed-collaborative-text-editor/formats/attributors';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import KEYS from '@/values/keys';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import { _HOMEWORKS_STORE } from '@/homework/values/homeworks.values';
import { MATHQUILL_FORMULA_OPERATORS } from '@/values/quill.values';
import { enableFormattingEmbeds } from '../../ed-collaborative-text-editor/collaborative-text.utils';
import EdEdupadHistory from '../ed-edupad-history/ed-edupad-history.vue';
declare const window: any;

@Component({
  name: 'ed-edupad-editor',
  components: {
    EdCollaborativeTextEditor: () => import('@/components/common/ed-collaborative-text-editor/ed-collaborative-text-editor.vue'),
    EdCollaborativeTextToolbar: () => import('@/components/common/ed-collaborative-text-editor/ed-collaborative-text-toolbar.vue'),
    EdEdupadHistory
  }
})

export default class EdEdupadEditor extends EdEdupad {
  @Prop({default:false}) readonly undoRedoOptions: boolean;
  @Prop({default:true})  readonly showToolbar: boolean;
  @Prop({default:false}) readonly readOnly: boolean;
  @Prop({default:false}) readonly mediaOptions: boolean;
  @Prop({default:false}) readonly enableFormulas: boolean;
  @Prop({default:false}) readonly pageStyle: boolean;
  @Prop({default:false}) readonly validateEditorEmpty: boolean;
  @Prop({default:true})  readonly showHistory: boolean;
  @Prop({default:'meetings'}) readonly collection: string;
  @Prop({}) readonly documentId: string;
  @Ref() readonly loading: any;
  @Ref() readonly reconnecting: any;

  
  public isOnline : boolean = false;
  public showUserStatus : boolean = false;
  
  private toolbarReady: boolean = false;
  private editorReady: boolean = false;

  public eduacPadClient: EduacPadClient = null;
  public quillEditor: any = {}; 
  private currentUserPossition : any;
  private timer : any;
  private mathquill4quillCacheKey: string; 
  getHTML() {
    return this.quillEditor.root.innerHTML;
  }

  get toolbarId() {
    return 'toolbar-' + this.dynamicId;
  } 
  get editorId(){
    return 'editor-' + this.dynamicId;
  }
  

  get hasTextEditorText() {
    return (Object.keys(this.quillEditor).length !== 0
      && this.quillEditor.getText(0).replaceAll(/\s/g,'').length > 0);
  }

  public loadToolbarFinished() {    
    this.toolbarReady = true;
    this.initQuill();
  }

  public loadEditorFinished() {
    this.editorReady = true;
    this.initQuill();
  }

  private validateEditorIsEmpty(delta: any) {
    this.textEditorEmpty(!this.hasTextEditorText);
  }

        
  private initQuill() {
    window.katex = katex;
    this.mathquill4quillCacheKey = `__mathquill4quill_cache__${this.collection}_${this.documentId}_${this.dynamicId}`;
    if(this.toolbarReady && this.editorReady) {
      setQuillInlineStyle();
      Quill.register('modules/cursors', QuillCursors);
      Quill.register({'modules/formula': mathquill4quill}, true);
      this.quillEditor =  new Quill(document.getElementById(this.editorId), {
        modules: {
          cursors: {
            hideDelayMs: 3000,
            hideSpeedMs: 3000,
          },
          formula: true,
          preserveFormatOnEnter: true,
          toolbar: `#${this.toolbarId}`,
          history: {
            userOnly: true
          }
        },
        placeholder: this.$t('edupad.placeholder').toString() ,
        theme: 'snow',
        readOnly: this.readOnly
      });

      this.quillEditor.clipboard.addMatcher(Node.TEXT_NODE, function (node, delta) {
        const Delta = Quill.import('delta');
        return new Delta().insert(node.data);
      });

      if(this.validateEditorEmpty) {
        this.quillEditor.on('text-change', this.validateEditorIsEmpty);
      }
      
      this.initFormulaEditor();
      
      if(this.documentId) 
        this.initEdupad()
      
      this.initQuillFinished();
    }

    
  }

  private initFormulaEditor() {
    const enableMathQuillFormulaAuthoring = mathquill4quill({ Quill });
    enableMathQuillFormulaAuthoring(this.quillEditor, {
      operators: MATHQUILL_FORMULA_OPERATORS,
      displayHistory: true,
      historyCacheKey: '__my_app_math_history_cachekey__',
      historySize: 20,
      cacheKey: this.mathquill4quillCacheKey
    });
    enableFormattingEmbeds(this.quillEditor);
  }
  
  private initEdupad(){
    this.showLoading()
    if(this.toolbarReady && this.editorReady && this.documentId) {
      this.eduacPadClient = new EduacPadClient({
        url: this.edupadUrl,
        socketIOOptions: this.socketIOOptions,
        editor:  this.quillEditor,
        username: this.username,
        document: {
          collection: this.collection,
          id: this.documentId,
          metadata: {
            page: this.pageSizes.letter,
          },
        },
        presence: {
          enabled: true,
          defaultColor: tinyColor.random().toHexString(),
        },
        attemptsToWorkOffline: 1
      });

      this.eduacPadClient.socketOn('connect', (data) => {
        this.hideLoading()
        this.eduacPadClient.setOnline();
        this.edupadOnline();
      });

      this.eduacPadClient.socketOn('disconnect', (data) => {
        this.eduacPadClient.setOffline();
        this.edupadOffline();
      });

      this.eduacPadClient.socketOn('document-deleted', (data) => {
          this.deleteDocument();
      });

      this.eduacPadClient.socketOn('connect_error', (data) => {
        if(data?.message === 'Invalid token'){
          this.showUserStatus = true;
          if(this.eduacPadClient.shareDbDoc?.data){        // The user is unable to authenticate but is able to save local deltas cause document is loaded
            this.$EdToast.create({ 
              msg:  `${this.$t('edupad.verificationFailedReconnect')}`,
              type: TOAST_TYPES.DANGER,
              top: '3rem',
              timeout: 5000
            });
          } else {                                        //The user is unable to authenticate and there is not document loaded, so is unable to save local deltas
            this.$EdModal.createMessageModal({
              title: this.$t('edupad.verificationFailed'),
              body: this.$t('edupad.errorMessage'),
              type: KEYS.EVENT_TYPES.DANGER,
              action: () =>  this.refresh()
            });
          }
        } 
        this.edupadOffline();
        this.eduacPadClient.setOffline();
      });

      this.eduacPadClient.socketOn('restore-version-start', (data) => {
        this.timer = performance.now();
        this.quillEditor.enable(false);
        this.showLoading(this.$t('edupad.remoteRestoring', data).toString())
      });

      this.eduacPadClient.socketOn('restore-version-end', async (data) => {
        await this.eduacPadClient.fetch();
        let time = performance.now() - this.timer;
        time = time > this.messageTime ? 1 : this.messageTime - time;
        setTimeout(() => { 
          this.quillEditor.enable(true);
          this.hideLoading();
          this.$EdToast.create({ 
            msg:  this.$t('edupad.remoteRestored', data).toString(),
            type: TOAST_TYPES.WARNING,
            top: '3rem',
            timeout: 5000
          });
        }, time)
      });

      // :::::::: Edupad events ::::::::
      this.eduacPadClient.on('sync-start', () => {
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_SYNCHRONISING_EDUPAD, true)
        this.$EdToast.create({ 
          msg:  `${this.$t('edupad.synchronizing')}`,
          type: TOAST_TYPES.WARNING,
          top: '3rem',
          timeout: 5000
        });
      });
      
      this.eduacPadClient.on('sync-end', () => {
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_SYNCHRONISING_EDUPAD, false)
      });

      this.eduacPadClient.on('sync-error', () => {
        this.$EdModal.createMessageModal({
          title: this.$t('courses.errors.errorTitle'),
          body: this.$t('edupad.errorMessage'),
          type: KEYS.EVENT_TYPES.DANGER,
          action: () => this.refresh()
        });
      });

      this.eduacPadClient.on('offline-start', () => {
        this.hideLoading()
        this.$EdToast.create({ 
          msg:  `${this.$t('edupad.offline')}`,
          type: TOAST_TYPES.WARNING,
          top: '3rem',
          timeout: 5000
        });
      });

      this.eduacPadClient.on('offline-stop', () => {
        this.reconnecting['show'](this.$t('edupad.offlineEnd'))      
        this.timer = performance.now();
        this.quillEditor.enable(false)
        this.currentUserPossition = this.quillEditor.getSelection(true);  //Save current user possition    
      });

      this.eduacPadClient.on('offline-end', () => {
        let time = performance.now() - this.timer;
        time = time > this.messageTime ? 1 : this.messageTime - time;
        setTimeout(() => {
          if (this.currentUserPossition) 
            this.quillEditor.setSelection(this.currentUserPossition.index, this.currentUserPossition.length)    //Restore user possition
          this.quillEditor.enable(true)
          this.reconnecting['hide']()
        }, time)
      });

      this.eduacPadClient.connect().then(() => {
        this.showUserStatus = false;
        let sharedbDoc = this.eduacPadClient.shareDbDoc;
        this.updatePageMetadata(sharedbDoc.metadata, this.quillEditor.container);
      });
    }
  }

  @Emit('edupadOnline')
  private edupadOnline(){
    this.isOnline = true;
  }

  @Emit('edupadOffline')
  private edupadOffline(){
    this.isOnline = false;
  }

  @Emit('deleteDocument')
  private deleteDocument(){
    this.eduacPadClient.disconnect();
    return true;
  }

  @Emit('refreshEdupad')
  private refresh(){
    // this.$router.push('/')
    this.eduacPadClient.disconnect();
    return true;
  }

 public onOpenVersionHistory(){
    if(this.isOnline){
      this.$EdModal.createBootstrapModal(EdEdupadHistory, {
        collection: this.collection,
        documentId: this.documentId
      })
    } else {
      this.$EdModal.createMessageModal({
        title: this.$t('errors.ups'),
        body: this.$t('edupad.errorHistory'),
        type: KEYS.EVENT_TYPES.DANGER,
      });
    }
  }

  private updatePageMetadata(metadata, domNode) {
    if (this.pageStyle && metadata && domNode) {
      const width = metadata?.page?.width ?? this.pageSizes.letter.w;
      domNode.style.minWidth = domNode.style.maxWidth = width + 'px';
    }
  }

  public showLoading(label?: string): void {
    try {
        if (this.loading) {
            if (label) {
                const i18Text = this.$i18n.t(label);
                this.loading['show'](i18Text);
            } else {
                this.loading['show']();
            }
        }
    } catch (e) {
        //
    }
  }

  public hideLoading(): void {
      if (this.loading) {
          this.loading['hide']();
      }
  }

  @Emit('initQuillFinished')
  private initQuillFinished() {
    return true;
  }

  @Emit('textEditorEmpty')
  private textEditorEmpty(data) {
    return data;
  }
  
  @Watch('documentId')
  private updateDocument(){
    this.initEdupad();
  }

  /* Hooks */
  destroyed() {
    this.eduacPadClient?.disconnect();
    this.$store.commit(_MEETINGS_STORE.MUTATIONS.SET_EDUPAD_DOCUMENT, '');
    try {
      localStorage.removeItem(this.mathquill4quillCacheKey);
    } catch (e) {
      // eslint-disable-line no-empty
    }
  }
}
